import { createSlotsStateFromBlocks } from './scene-state';
import {
  getSlotKeyFromPlace,
  resolve40ftPlaceSlotKeys,
  resolvePlaceSlotKeys,
  slotKeyToPlaceKey,
  type YardBlockOption,
} from './place-utils';
import { type SlotsState } from './slots';

export const MAX_CONTAINER_TIER = 20;

/** Backend interprets tier 20 as "place on top" (auto stack). Always send this value in editContainer. */
export const CONTAINER_PLACE_API_TIER = 20;

export const YARD_PLACE_SCENE_MESSAGES = {
  twoBottomSlots:
    'For a 40-foot container, the two bottom slots must be occupied by the same length.',
  notEnoughSlots: 'For this container, there are not enough adjacent slots.',
  invalidTier: 'The selected tier is not valid for this location.',
} as const;

export const parseSceneToSlotsState = (blocksScene: string): SlotsState => {
  try {
    const parsed = JSON.parse(blocksScene || '{}');

    if (Array.isArray(parsed?.blocks)) {
      return createSlotsStateFromBlocks(parsed.blocks, parsed.containers ?? []);
    }

    return {
      containers: parsed?.containers ?? [],
      slotNames: parsed?.slotNames,
      blockMaps: parsed?.blockMaps,
    };
  } catch {
    return { containers: [] };
  }
};

type ValidateContainerPlaceParams = {
  slotsState: SlotsState;
  containerId: string;
  containerLength?: number;
  block: string;
  row: string;
  slot: string;
  previousPlace?: {
    block: string;
    row: string;
    slot: string;
    tier: string;
  };
};

const occupiesSlot = (item: SlotsState['containers'][number], slotKey: string) =>
  item.slotKeys?.includes(slotKey) ?? item.slotKey === slotKey;

const getOccupiedTiers = (
  containers: SlotsState['containers'],
  slotKeys: string[],
  excludeContainerId: string,
) =>
  slotKeys.map((slotKey) =>
    containers
      .filter((item) => item.id !== excludeContainerId && occupiesSlot(item, slotKey))
      .reduce((maxTier, item) => Math.max(maxTier, item.tier), 0),
  );

const getSlotStack = (
  containers: SlotsState['containers'],
  slotKey: string,
  excludeContainerId: string,
) =>
  containers
    .filter((item) => item.id !== excludeContainerId && occupiesSlot(item, slotKey))
    .slice()
    .sort((left, right) => left.tier - right.tier);

const isEven40ftSupport = (
  containers: SlotsState['containers'],
  slotKeys: string[],
  excludeContainerId: string,
) => {
  const stacks = slotKeys.map((slotKey) => getSlotStack(containers, slotKey, excludeContainerId));
  const occupiedTiers = stacks.map((stack) =>
    stack.reduce((maxTier, item) => Math.max(maxTier, item.tier), 0),
  );

  if (!occupiedTiers.every((itemTier) => itemTier === occupiedTiers[0])) return false;

  const maxTier = occupiedTiers[0];
  if (maxTier === 0) return true;

  const topContainers = stacks.map((stack) => stack.find((item) => item.tier === maxTier));
  const topContainer = topContainers[0];

  if (
    topContainer?.length === 40 &&
    topContainers.every((item) => item?.id === topContainer.id)
  ) {
    return true;
  }

  if (!stacks.every((stack) => stack.length === stacks[0].length)) return false;

  for (let tier = 1; tier <= maxTier; tier++) {
    const lengths = stacks.map((stack) => stack.find((item) => item.tier === tier)?.length ?? 0);

    if (!lengths[0] || !lengths.every((itemLength) => itemLength === lengths[0])) return false;
  }

  return true;
};

const resolveTargetSlotKeys = (
  slotsState: SlotsState,
  block: string,
  row: string,
  slot: string,
  containerLength: number,
) => {
  const slotNames = slotsState.slotNames ?? {};
  const blockMaps = slotsState.blockMaps ?? [];

  if (containerLength !== 40) {
    const targetSlotKey = getSlotKeyFromPlace(blockMaps, block, row, slot);

    if (!targetSlotKey || !slotNames[targetSlotKey]) {
      return { error: YARD_PLACE_SCENE_MESSAGES.notEnoughSlots as string };
    }

    return { targetSlotKeys: [targetSlotKey] };
  }

  const targetSlotKeys = resolve40ftPlaceSlotKeys(blockMaps, slotNames, block, row, slot);

  if (targetSlotKeys.length !== 2) {
    return { error: YARD_PLACE_SCENE_MESSAGES.notEnoughSlots };
  }

  return { targetSlotKeys };
};

export const getNextContainerTier = ({
  slotsState,
  containerId,
  containerLength = 20,
  block,
  row,
  slot,
}: ValidateContainerPlaceParams): number | null => {
  const containers = slotsState.containers;
  const resolvedTarget = resolveTargetSlotKeys(slotsState, block, row, slot, containerLength);

  if ('error' in resolvedTarget) return null;

  const occupiedTiers = getOccupiedTiers(containers, resolvedTarget.targetSlotKeys, containerId);

  return Math.max(0, ...occupiedTiers) + 1;
};

export const validateContainerPlace = ({
  slotsState,
  containerId,
  containerLength = 20,
  block,
  row,
  slot,
  previousPlace,
}: ValidateContainerPlaceParams): string | null => {
  const containers = slotsState.containers;
  const existingContainer = containers.find((item) => item.id === containerId);

  if (existingContainer && previousPlace?.block) {
    const sourceSlotKeys = resolvePlaceSlotKeys(
      slotsState,
      previousPlace.block,
      previousPlace.row,
      previousPlace.slot,
      containerLength,
    );
    const resolvedSourceSlotKeys = sourceSlotKeys.length
      ? sourceSlotKeys
      : existingContainer.slotKeys ?? [existingContainer.slotKey];

    const hasContainersAbove = containers.some((item) => {
      if (item.id === containerId || item.tier <= existingContainer.tier || item.length !== 40) {
        return false;
      }

      const itemSlotKeys = item.slotKeys ?? [item.slotKey];
      return itemSlotKeys.some((slotKey) => resolvedSourceSlotKeys.includes(slotKey));
    });

    if (hasContainersAbove) {
      return YARD_PLACE_SCENE_MESSAGES.twoBottomSlots;
    }
  }

  const resolvedTarget = resolveTargetSlotKeys(slotsState, block, row, slot, containerLength);

  if ('error' in resolvedTarget) {
    return resolvedTarget.error ?? YARD_PLACE_SCENE_MESSAGES.notEnoughSlots;
  }

  const { targetSlotKeys } = resolvedTarget;
  const occupiedTiers = getOccupiedTiers(containers, targetSlotKeys, containerId);

  if (containerLength === 40 && !isEven40ftSupport(containers, targetSlotKeys, containerId)) {
    return YARD_PLACE_SCENE_MESSAGES.twoBottomSlots;
  }

  const nextTier = Math.max(0, ...occupiedTiers) + 1;

  if (nextTier > MAX_CONTAINER_TIER) {
    return YARD_PLACE_SCENE_MESSAGES.invalidTier;
  }

  return null;
};

export const isContainerPlaceAvailable = (
  slotsState: SlotsState,
  containerId: string,
  containerLength: number,
  block: string,
  row: string,
  slot: string,
): boolean =>
  validateContainerPlace({
    slotsState,
    containerId,
    containerLength,
    block,
    row,
    slot,
  }) === null;

export type GetYardBlocksFromBlocksSceneOptions = {
  containerLength?: number;
  excludeContainerId?: string;
  selectedBlock?: string;
  selectedRow?: string;
};

export type ContainerPlaceFromScene = {
  block: string;
  row: string;
  slot: string;
  tier: string;
};

const parsePlaceKey = (placeKey?: string): ContainerPlaceFromScene | null => {
  const match = placeKey ? /^(.+)-(\d+)-(\d+)$/.exec(placeKey) : null;
  if (!match) return null;

  return {
    block: match[1],
    row: match[2],
    slot: match[3],
    tier: '',
  };
};

export const getContainerPlaceFromBlocksScene = (
  blocksScene: string,
  containerId: string,
): ContainerPlaceFromScene | null => {
  if (!blocksScene || !containerId) return null;

  const slotsState = parseSceneToSlotsState(blocksScene);
  const container = slotsState.containers.find((item) => item.id === containerId);
  if (!container) return null;

  const blockMaps = slotsState.blockMaps ?? [];
  const placeFromSlotKey = parsePlaceKey(slotKeyToPlaceKey(container.slotKey, blockMaps));

  if (placeFromSlotKey) {
    return {
      ...placeFromSlotKey,
      tier: String(container.tier),
    };
  }

  try {
    const parsed = JSON.parse(blocksScene || '{}');
    const raw = parsed.containers?.find((item: { id: string }) => item.id === containerId);
    const placeFromKey = parsePlaceKey(raw?.key?.split(',')[0]?.trim());

    if (!placeFromKey) return null;

    return {
      ...placeFromKey,
      tier: String(container.tier ?? raw?.tier ?? ''),
    };
  } catch {
    return null;
  }
};

const parseBlocksConfigFromScene = (blocksScene: string): YardBlockOption[] => {
  try {
    const parsed = JSON.parse(blocksScene || '{}');
    const blocks = parsed?.blocks ?? [];

    return blocks
      .filter(
        (item: { rowsLength?: number; slotsLength?: number }) =>
          (item.rowsLength ?? 0) > 0 && (item.slotsLength ?? 0) > 0,
      )
      .map((item: { name: string; rowsLength: number; slotsLength: number }) => ({
        block: item.name,
        rows: Array.from({ length: item.rowsLength }, (_, index) => String(index + 1)),
        slots: Array.from({ length: item.slotsLength }, (_, index) => String(index + 1)),
      }));
  } catch {
    return [];
  }
};

export const getAvailable40ftSlotsForRow = (
  blocksScene: string,
  block: string,
  row: string,
  excludeContainerId = '',
): string[] => {
  const slotsState = parseSceneToSlotsState(blocksScene);
  const blockConfig = parseBlocksConfigFromScene(blocksScene).find((item) => item.block === block);

  if (!blockConfig) return [];

  return blockConfig.slots.filter((slot) =>
    isContainerPlaceAvailable(slotsState, excludeContainerId, 40, block, row, slot),
  );
};

export const getYardBlocksFromBlocksScene = (
  blocksScene: string,
  {
    containerLength = 20,
    excludeContainerId = '',
    selectedBlock,
    selectedRow,
  }: GetYardBlocksFromBlocksSceneOptions = {},
): YardBlockOption[] => {
  const allBlocks = parseBlocksConfigFromScene(blocksScene);
  const length = Number(containerLength) || 20;

  if (length !== 40) {
    return allBlocks;
  }

  const slotsState = parseSceneToSlotsState(blocksScene);
  const selectedRowValue = selectedRow != null && selectedRow !== '' ? String(selectedRow) : '';

  return allBlocks
    .map((blockOption) => {
      const maxRow = blockOption.rows.length;
      const availableRows = blockOption.rows.filter((rowItem) => {
        if (Number(rowItem) >= maxRow) return false;

        return blockOption.slots.some((slot) =>
          isContainerPlaceAvailable(
            slotsState,
            excludeContainerId,
            40,
            blockOption.block,
            rowItem,
            slot,
          ),
        );
      });

      const slots =
        selectedBlock === blockOption.block && selectedRowValue
          ? blockOption.slots.filter((slot) =>
              isContainerPlaceAvailable(
                slotsState,
                excludeContainerId,
                40,
                blockOption.block,
                selectedRowValue,
                slot,
              ),
            )
          : [];

      return {
        block: blockOption.block,
        rows: availableRows,
        slots,
      };
    })
    .filter((blockOption) => blockOption.rows.length > 0);
};
